import React, { useEffect, useState, useRef } from "react"
import MetaTags from "react-meta-tags"
import PropTypes from "prop-types"
import { withRouter, Link } from "react-router-dom"
import { isEmpty } from "lodash"
import BootstrapTable from "react-bootstrap-table-next"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator"
import * as Yup from "yup"
import { useFormik } from "formik"
import DeleteModal from "../../components/Common/DeleteModal"

import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit"

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Badge,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap"

//redux
import { useSelector, useDispatch } from "react-redux"

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"

import {
  getBusinesses as onGetBusinesses,
  getCompanies as onGetCompanies,
  addNewBusiness as onAddNewBusiness,
  updateBusiness as onUpdateBusiness,
  deleteBusiness as onDeleteBusiness,
} from "store/actions"
import BusinessDetailsModal from "./BusinessDetailsModal"

const Businesses = props => {
  const dispatch = useDispatch()

  const [businessList, setBusinessList] = useState([])
  const [companyList, setCompanyList] = useState([])
  const [business, setBusiness] = useState(null)
  const [modal, setModal] = useState(false)
  const [modalViewDetails, setViewDetailsModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)

  const { businesses } = useSelector(state => ({
    businesses: state.businesses.businesses,
  }))

  const { companies } = useSelector(state => ({
    companies: state.companies.companies,
  }))

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      _id: (business && business._id) || "",
      name: (business && business.name) || "",
      description: (business && business.description) || "",
      company: (business && business.company) || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Your Business Name"),
      description: Yup.string().required(
        "Please Enter Your Business Description"
      ),
      company: Yup.string().required("Please Select a Company"),
    }),
    onSubmit: values => {
      if (isEdit) {
        const updateBusiness = {
          _id: business._id,
          name: values.name,
          description: values.description,
        }
        // update business
        dispatch(onUpdateBusiness(updateBusiness))
        validation.resetForm()
      } else {
        const newBusiness = {
          name: values["name"],
          description: values["description"],
          company: values["company"],
        }
        // save new business
        dispatch(onAddNewBusiness(newBusiness))
        validation.resetForm()
      }
      toggle()
    },
  })

  const selectRow = {
    mode: "checkbox",
  }

  //pagination customization
  const pageOptions = {
    sizePerPage: 10,
    totalSize: businesses.length, // replace later with size(businesses),
    custom: true,
  }
  const { SearchBar } = Search

  const BusinessColumns = toggleModal => [
    {
      dataField: "name",
      text: "Name",
      sort: true,
    },
    {
      dataField: "description",
      text: "Description",
      sort: true,
    },
    {
      dataField: "companyName",
      text: "Company",
      sort: true,
    },
    {
      dataField: "deleted",
      text: "Status",
      sort: true,
      // eslint-disable-next-line react/display-name
      formatter: (cellContent, row) => (
        <Badge
          className={"font-size-12 badge-soft-" + row.badgeclass}
          color={row.deleted ? "danger" : "success"}
          pill
        >
          {row.deleted ? "Deleted" : "Active"}
        </Badge>
      ),
    },
    {
      dataField: "view",
      isDummyField: true,
      text: "View Details",
      sort: true,
      // eslint-disable-next-line react/display-name
      formatter: (cellContent, business) => (
        <Button
          type="button"
          color="primary"
          className="btn-sm btn-rounded"
          onClick={() => handleBusinessDetails(business)}
        >
          View Details
        </Button>
      ),
    },
    {
      dataField: "action",
      isDummyField: true,
      text: "Action",
      // eslint-disable-next-line react/display-name
      formatter: (cellContent, business) => (
        <>
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-success"
              onClick={() => handleBusinessClick(business)}
            >
              <i className="mdi mdi-pencil font-size-18" id="edittooltip" />
              <UncontrolledTooltip placement="top" target="edittooltip">
                Edit
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => onClickDelete(business)}
            >
              <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
              <UncontrolledTooltip placement="top" target="deletetooltip">
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        </>
      ),
    },
  ]

  useEffect(() => {
    if (businesses && !businesses.length) {
      dispatch(onGetBusinesses())
    }
  }, [dispatch, businesses])

  useEffect(() => {
    if (companies && !companies.length) {
      dispatch(onGetCompanies())
    }
  }, [dispatch, companies])

  useEffect(() => {
    if (companies) {
      const newCompanies = companies.filter(company => !company.deleted)
      setCompanyList(newCompanies)
    }
  }, [companies])

  useEffect(() => {
    if (companies && businesses) {
      const newBusinesses = businesses
        .filter(business => !business.deleted)
        .map(business => ({
          ...business,
          companyName: companies.find(
            company => company._id === business.company
          )?.name,
        }))
      setBusinessList(newBusinesses)
    }
  }, [businesses, companies])

  const toggle = () => {
    if (modal) {
      setModal(false)
      setBusiness(null)
    } else {
      setModal(true)
    }
  }

  const toggleViewModal = () => {
    if (modalViewDetails) {
      setViewDetailsModal(false)
      setBusiness(null)
    } else {
      setViewDetailsModal(true)
    }
  }

  const handleBusinessClick = arg => {
    const business = arg

    setBusiness({
      _id: business._id,
      name: business.name,
      description: business.description,
      company: business.company,
      deleted: business.deleted,
      badgeclass: business.badgeclass,
    })

    setIsEdit(true)

    toggle()
  }

  const handleBusinessDetails = arg => {
    const business = arg

    setBusiness({
      _id: business._id,
      name: business.name,
      description: business.description,
      company: business.company,
      companyName: business.companyName,
      deleted: business.deleted,
      badgeclass: business.badgeclass,
    })

    toggleViewModal()
  }

  var node = useRef()
  const onPaginationPageChange = page => {
    if (
      node &&
      node.current &&
      node.current.props &&
      node.current.props.pagination &&
      node.current.props.pagination.options
    ) {
      node.current.props.pagination.options.onPageChange(page)
    }
  }

  //delete business
  const [deleteModal, setDeleteModal] = useState(false)

  const onClickDelete = business => {
    setBusiness(business)
    setDeleteModal(true)
  }

  const handleDeleteBusiness = () => {
    if (business._id) {
      dispatch(onDeleteBusiness(business))
      onPaginationPageChange(1)
      setDeleteModal(false)
    }
  }
  const handleBusinessClicks = () => {
    setIsEdit(false)
    toggle()
  }

  const defaultSorted = [
    {
      dataField: "_id",
      business: "desc",
    },
  ]
  return (
    <React.Fragment>
      <BusinessDetailsModal
        isOpen={modalViewDetails}
        toggle={toggleViewModal}
        business={business}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteBusiness}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <MetaTags>
          <title>Businesses</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Businesses" breadcrumbItem="Businesses" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(pageOptions)}
                    keyField="id"
                    columns={BusinessColumns(toggle)}
                    data={businessList}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        data={businessList}
                        columns={BusinessColumns(toggle)}
                        bootstrap4
                        search
                      >
                        {toolkitProps => (
                          <React.Fragment>
                            <Row className="mb-2">
                              <Col sm="4">
                                <div className="search-box me-2 mb-2 d-inline-block">
                                  <div className="position-relative">
                                    <SearchBar {...toolkitProps.searchProps} />
                                    <i className="bx bx-search-alt search-icon" />
                                  </div>
                                </div>
                              </Col>
                              <Col sm="8">
                                <div className="text-sm-end">
                                  <Button
                                    type="button"
                                    color="success"
                                    className="btn-rounded  mb-2 me-2"
                                    onClick={handleBusinessClicks}
                                  >
                                    <i className="mdi mdi-plus me-1" />
                                    Add Business
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col xl="12">
                                <div className="table-responsive">
                                  <BootstrapTable
                                    keyField="id"
                                    responsive
                                    bbusinessed={false}
                                    striped={false}
                                    defaultSorted={defaultSorted}
                                    selectRow={selectRow}
                                    classes={
                                      "table align-middle table-nowrap table-check"
                                    }
                                    headerWrapperClasses={"table-light"}
                                    {...toolkitProps.baseProps}
                                    {...paginationTableProps}
                                    ref={node}
                                  />
                                </div>
                                <Modal isOpen={modal} toggle={toggle}>
                                  <ModalHeader toggle={toggle} tag="h4">
                                    {!!isEdit
                                      ? "Edit Business"
                                      : "Add Business"}
                                  </ModalHeader>
                                  <ModalBody>
                                    <Form
                                      onSubmit={e => {
                                        e.preventDefault()
                                        validation.handleSubmit()
                                        return false
                                      }}
                                    >
                                      <Row form>
                                        <Col className="col-12">
                                          <div className="mb-3">
                                            <Label className="form-label">
                                              Business Name
                                            </Label>
                                            <Input
                                              name="name"
                                              type="text"
                                              validate={{
                                                required: { value: true },
                                              }}
                                              onChange={validation.handleChange}
                                              onBlur={validation.handleBlur}
                                              value={
                                                validation.values.name || ""
                                              }
                                              invalid={
                                                validation.touched.name &&
                                                validation.errors.name
                                                  ? true
                                                  : false
                                              }
                                            />
                                            {validation.touched.name &&
                                            validation.errors.name ? (
                                              <FormFeedback type="invalid">
                                                {validation.errors.name}
                                              </FormFeedback>
                                            ) : null}
                                          </div>
                                          <div className="mb-3">
                                            <Label className="form-label">
                                              Business Description
                                            </Label>
                                            <Input
                                              name="description"
                                              type="textarea"
                                              id="textarea"
                                              maxLength="225"
                                              rows="3"
                                              validate={{
                                                required: { value: true },
                                              }}
                                              onChange={validation.handleChange}
                                              onBlur={validation.handleBlur}
                                              value={
                                                validation.values.description ||
                                                ""
                                              }
                                              invalid={
                                                validation.touched
                                                  .description &&
                                                validation.errors.description
                                                  ? true
                                                  : false
                                              }
                                            />
                                            {validation.touched.description &&
                                            validation.errors.description ? (
                                              <FormFeedback type="invalid">
                                                {validation.errors.description}
                                              </FormFeedback>
                                            ) : null}
                                          </div>
                                          {!isEdit && (
                                            <div className="mb-3">
                                              <Label className="form-label">
                                                Company
                                              </Label>
                                              <Input
                                                name="company"
                                                type="select"
                                                className="form-select"
                                                onChange={
                                                  validation.handleChange
                                                }
                                                onBlur={validation.handleBlur}
                                                value={
                                                  validation.values.company ||
                                                  ""
                                                }
                                              >
                                                <option value="" key="none">
                                                  Select a Company
                                                </option>
                                                {companyList.map(
                                                  (company, index) => (
                                                    <option
                                                      value={company._id}
                                                      key={company._id}
                                                    >
                                                      {company.name}
                                                    </option>
                                                  )
                                                )}
                                              </Input>
                                            </div>
                                          )}
                                        </Col>
                                      </Row>
                                      <Row>
                                        <Col>
                                          <div className="text-end">
                                            <button
                                              type="submit"
                                              className="btn btn-success save-user"
                                            >
                                              Save
                                            </button>
                                          </div>
                                        </Col>
                                      </Row>
                                    </Form>
                                  </ModalBody>
                                </Modal>
                              </Col>
                            </Row>
                            <Row className="align-items-md-center mt-30">
                              <Col className="pagination pagination-rounded justify-content-end mb-2 inner-custom-pagination">
                                <PaginationListStandalone
                                  {...paginationProps}
                                />
                              </Col>
                            </Row>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

Businesses.propTypes = {
  businesses: PropTypes.array,
  onGetBusinesses: PropTypes.func,
  onAddNewBusiness: PropTypes.func,
  onDeleteBusiness: PropTypes.func,
  onUpdateBusiness: PropTypes.func,
}

export default withRouter(Businesses)
